import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { getServerTime } from "features/auth/usecases/AuthUsecases";
import { setAlertCheckout } from "../shared/store/headerSlice";
import { setDayType, setServerTime } from "../features/auth/presentation/store/authSlice";

// Constants for better maintainability
export const STORAGE_KEYS = {
    CHECKIN_TIME: 'checkinTime',
    CHECKOUT_TIME: 'checkoutTime',
    ATTENDANCE_NOTE: 'attendanceNote',
    LEAVE_WORK_NOTE: 'leaveWorkNote',
};

const ROUTES = {
    TIME_ATTENDANCE: '/time_attendance',
};

const INTERVALS = {
    CHECK_TIME: 10 * 60 * 1000, // 10 minutes
    UPDATE_TIME: 60 * 1000, // 1 minute
};

/**
 * Checks if the current time has passed the specified time.
 * @param {string} timeString - Time in "HH:mm" format (e.g., "14:30").
 * @param {DateTime} serverTime - Server time (Luxon DateTime).
 * @returns {boolean} True if the current time has passed the specified time.
 */
const hasTimePassed = (timeString, serverTime) => {
    if (!timeString || !serverTime || !(serverTime instanceof DateTime)) return false;

    const [targetHours, targetMinutes] = timeString.split(':').map(Number);
    if (isNaN(targetHours) || isNaN(targetMinutes)) return false;

    const now = new Date(serverTime.toISO());
    return now.getHours() > targetHours ||
        (now.getHours() === targetHours && now.getMinutes() >= targetMinutes);
};

/**
 * Validates the date string.
 * @param {string} date - Date in "yyyy-MM-dd" format.
 * @returns {boolean} True if the date is valid.
 */
const isValidDate = (date) => date && date !== 'Invalid DateTime';

/**
 * Clears attendance-related localStorage keys and navigates.
 * @param {Function} navigate - Navigation function from react-router-dom.
 */
const clearLocalStorageAndNavigate = (navigate) => {
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
    navigate(ROUTES.TIME_ATTENDANCE, { replace: true });
};

/**
 * Checks working hours and updates alert state.
 * @param {Object} user - User data from Redux store.
 * @param {DateTime} serverTime - Server time.
 * @param {string} checkinTime - Check-in time from localStorage.
 * @param {string} checkoutTime - Check-out time from localStorage.
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Function} navigate - Navigation function from react-router-dom.
 */
const checkWorkingHours = (user, serverTime, checkinTime, checkoutTime, dispatch, navigate, dayType) => {
    const serverDate = serverTime.toFormat('yyyy-MM-dd');
    const checkinDate = checkinTime?.split(' ')[0];

    if (!isValidDate(serverDate)) return;

    if (checkinDate != serverDate) {
        clearLocalStorageAndNavigate(navigate);
        return;
    }

    let weekdayHours = user?.parking?.weekdayOperatingHours;

    if (dayType == 'WEEKEND' && user?.parking?.weekendOperatingHours) {
        weekdayHours = user?.parking?.weekendOperatingHours;
    } else if (dayType == 'HOLIDAY' && user?.parking?.holidayOperatingHours) {
        weekdayHours = user?.parking?.holidayOperatingHours;
    }

    if (!weekdayHours) return;

    const [endHour, endMinute] = weekdayHours?.split('~')[1]?.trim()?.split(':') || [];

    if (!endHour || !endMinute) {
        console.warn('Invalid operating hours format');
        return;
    }

    const isPassed = hasTimePassed(`${endHour}:${endMinute}`, serverTime) ||
        (checkinDate !== serverDate && checkoutTime !== null);
    dispatch(setAlertCheckout(isPassed));
};

/**
 * Custom hook to manage attendance and server time checking.
 * @param {Object} user - User data from Redux store.
 * @param {DateTime} serverTime - Server time from Redux store.
 * @param {boolean} isLoggedIn - Login state from Redux store.
 * @param {Function} dispatch - Redux dispatch function.
 * @returns {void}
 */
const useAttendanceCheck = (user, serverTime, isLoggedIn, dispatch, dayType) => {
    const navigate = useNavigate();

    const checkTime = useCallback(async () => {
        try {
            const checkinTime = localStorage.getItem(STORAGE_KEYS.CHECKIN_TIME);
            const checkoutTime = localStorage.getItem(STORAGE_KEYS.CHECKOUT_TIME);

            if (!checkinTime && user?.userId) {
                navigate(ROUTES.TIME_ATTENDANCE, { replace: true });
                return;
            }

            const serverTimeResponse = await getServerTime();
            const newServerTime = DateTime.fromISO(
                serverTimeResponse?.ISO || DateTime.now().setZone('Asia/Seoul'),
                { setZone: true }
            );
            dispatch(setServerTime(newServerTime.isValid ? newServerTime : DateTime.now().setZone('Asia/Seoul')));
            dispatch(setDayType(serverTimeResponse?.dayType || 'WEEKDAY'));

            if (checkinTime) {

                checkWorkingHours(user, newServerTime, checkinTime, checkoutTime, dispatch, navigate, dayType);
            }
        } catch (error) {
            console.error('Error checking time:', error);
            // TODO: Dispatch action to show user-facing error notification
        }
    }, [user, dispatch, navigate]);

    // Periodic time check when user is logged in
    useEffect(() => {
        let intervalId;
        checkTime();
        intervalId = setInterval(checkTime, INTERVALS.CHECK_TIME);

        return () => clearInterval(intervalId);
    }, [isLoggedIn]);

    // Update server time every minute
    useEffect(() => {
        let intervalId;
        if (serverTime) {
            intervalId = setInterval(() => {
                const updatedServerTime = serverTime instanceof DateTime
                    ? serverTime.plus({ seconds: 60 })
                    : DateTime.now().setZone('Asia/Seoul');
                dispatch(setServerTime(updatedServerTime));

                const checkinTime = localStorage.getItem(STORAGE_KEYS.CHECKIN_TIME);
                const checkoutTime = localStorage.getItem(STORAGE_KEYS.CHECKOUT_TIME);

                if (checkinTime) {
                    checkWorkingHours(user, updatedServerTime, checkinTime, checkoutTime, dispatch, navigate);
                } else if (user?.userId) {
                    navigate(ROUTES.TIME_ATTENDANCE, { replace: true });
                }
            }, INTERVALS.UPDATE_TIME); // 1 minute
        }
        return () => clearInterval(intervalId);
    }, [serverTime]);
};

export default useAttendanceCheck;